import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  FileText,
  Layers,
  Loader2,
  Ruler,
  Warehouse,
  XCircle,
} from 'lucide-react';
import { BATIMENT_STATUTS, BATIMENT_TYPES } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import {
  EMPTY_REPORTING_FILTERS,
  ReportingFilters,
  type ReportingFilterValues,
} from '../../components/reporting/ReportingFilters';
import { ExportButtons } from '../../components/reporting/ExportButtons';
import {
  STATUT_BATIMENT_LABEL,
  TYPE_BATIMENT_LABEL,
  formatNumber,
  formatPercent,
} from '../../components/reporting/format';
import type { InfrastructureReportResult } from '../../components/reporting/types';

/**
 * Page `/reporting/infrastructure` (US-REP-03).
 *
 *  Filtres : bâtiment, type, statut.
 *
 *  Sections :
 *   - Cards résumé : totaux bâtiments/étages/salles + surface totale.
 *   - Tableau occupation par bâtiment avec barre de progression visuelle.
 *   - Cards conformité ERP (par type document, barres valides/expirés).
 *   - Indicateurs documents expirants + bâtiments sans documents obligatoires.
 */

export function InfrastructureReportPage() {
  const [filters, setFilters] = useState<ReportingFilterValues>({
    ...EMPTY_REPORTING_FILTERS,
  });
  const [data, setData] = useState<InfrastructureReportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [batiments, setBatiments] = useState<Array<{ id: string; code: string; nom: string }>>([]);

  const printRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api
      .get<{ data: Array<{ id: string; code: string; nom: string }> }>('/batiments?page_size=200')
      .then((res) => setBatiments(res.data.data))
      .catch(() => setBatiments([]));
  }, []);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.batiment_id) p.set('batiment_id', filters.batiment_id);
    if (filters.type) p.set('type', filters.type);
    if (filters.statut) p.set('statut', filters.statut);
    return p;
  }, [filters.batiment_id, filters.type, filters.statut]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.toString()]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const qs = queryParams.toString();
      const res = await api.get<{ data: InfrastructureReportResult }>(
        `/reporting/infrastructure${qs ? `?${qs}` : ''}`,
      );
      setData(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  // Bâtiments sans documents obligatoires : on n'a pas la liste détaillée
  // (juste le compteur) — on affiche le compteur dans la section conformité.

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Building2 className="w-7 h-7 mr-2 text-primary-600" />
            Rapport Infrastructure
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Occupation, surfaces et conformité ERP par bâtiment.
          </p>
        </div>
        <ExportButtons
          excelUrl="/reporting/infrastructure.xlsx"
          excelParams={queryParams}
          filename="rapport-infrastructure"
          targetRef={printRef}
          pdfDisabled={!data}
        />
      </div>

      <ReportingFilters
        values={filters}
        onChange={setFilters}
        onRefresh={refresh}
        fields={{ batiment: true, type: true, statut: true }}
        batimentsOptions={batiments.map((b) => ({
          value: b.id,
          label: `${b.code} — ${b.nom}`,
        }))}
        typeOptions={BATIMENT_TYPES.map((t) => ({
          value: t,
          label: TYPE_BATIMENT_LABEL[t] ?? t,
        }))}
        statutOptions={BATIMENT_STATUTS.map((s) => ({
          value: s,
          label: STATUT_BATIMENT_LABEL[s] ?? s,
        }))}
        typeLabel="Type bâtiment"
      />

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center p-24 bg-white rounded-lg shadow-sm border border-gray-200">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
        </div>
      ) : data ? (
        <div ref={printRef} className="space-y-4">
          {/* Cards résumé */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              label="Bâtiments"
              value={formatNumber(data.resume.total_batiments)}
              icon={Building2}
              tone="primary"
            />
            <SummaryCard
              label="Étages"
              value={formatNumber(data.resume.total_etages)}
              icon={Layers}
              tone="blue"
            />
            <SummaryCard
              label="Salles"
              value={formatNumber(data.resume.total_salles)}
              icon={Warehouse}
              tone="purple"
            />
            <SummaryCard
              label="Surface totale"
              value={`${formatNumber(Math.round(data.resume.surface_totale))} m²`}
              icon={Ruler}
              tone="orange"
            />
          </div>

          {/* Surface par type bâtiment */}
          {data.resume.surface_par_type_batiment.length > 0 && (
            <Panel title="Surface par type de bâtiment">
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                {data.resume.surface_par_type_batiment.map((s) => (
                  <li
                    key={s.type}
                    className="flex items-center justify-between border border-gray-100 rounded-lg p-3 bg-gray-50"
                  >
                    <span className="text-gray-700 font-medium">
                      {TYPE_BATIMENT_LABEL[s.type] ?? s.type}
                    </span>
                    <span className="tabular-nums text-gray-900 font-semibold">
                      {formatNumber(Math.round(s.surface_totale))} m²
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {/* Tableau occupation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Occupation par bâtiment</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Code</Th>
                    <Th>Nom</Th>
                    <Th right>Étages</Th>
                    <Th right>Salles</Th>
                    <Th right>Surface totale</Th>
                    <Th right>Surface occupée</Th>
                    <Th>Taux d'occupation</Th>
                    <Th right>Réservables</Th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {data.occupation.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                        Aucun bâtiment pour ces filtres.
                      </td>
                    </tr>
                  ) : (
                    data.occupation.map((o) => (
                      <tr key={o.batiment_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-primary-600 whitespace-nowrap">
                          {o.code}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 max-w-[220px] truncate"
                          title={o.nom}
                        >
                          {o.nom}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatNumber(o.nb_etages)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatNumber(o.nb_salles)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatNumber(Math.round(o.surface_totale))} m²
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatNumber(Math.round(o.surface_occupee))} m²
                        </td>
                        <td className="px-3 py-2 min-w-[180px]">
                          <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  o.taux_occupation_pct >= 90
                                    ? 'bg-red-500'
                                    : o.taux_occupation_pct >= 70
                                      ? 'bg-orange-500'
                                      : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(100, o.taux_occupation_pct)}%` }}
                              />
                            </div>
                            <span className="text-[11px] text-gray-600 tabular-nums w-12 text-right">
                              {formatPercent(o.taux_occupation_pct, 0)}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatNumber(o.salles_reservables)} /{' '}
                          {formatNumber(o.salles_non_reservables)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Conformité ERP */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Conformité documentaire par type">
              {data.conformite_erp.documents_par_type.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">Aucun document référencé.</p>
              ) : (
                <ul className="space-y-3">
                  {data.conformite_erp.documents_par_type.map((d) => {
                    const total = d.total === 0 ? 1 : d.total;
                    const pctValides = (d.valides / total) * 100;
                    const pctExpires = (d.expires / total) * 100;
                    return (
                      <li key={d.type}>
                        <div className="flex items-center justify-between text-xs mb-0.5">
                          <span className="font-medium text-gray-700">{d.type}</span>
                          <span className="text-gray-600 tabular-nums">
                            {formatNumber(d.valides)} valides · {formatNumber(d.expires)} expirés
                          </span>
                        </div>
                        <div className="flex w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="bg-green-500"
                            style={{ width: `${pctValides}%` }}
                            title={`${d.valides} valides`}
                          />
                          <div
                            className="bg-red-500"
                            style={{ width: `${pctExpires}%` }}
                            title={`${d.expires} expirés`}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>

            <Panel title="Alertes documentaires">
              <ul className="space-y-3 text-sm">
                <li className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <span className="text-yellow-900 inline-flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Documents expirants sous 30 jours
                  </span>
                  <span className="font-bold text-yellow-900 tabular-nums">
                    {formatNumber(data.conformite_erp.documents_expirants_30j)}
                  </span>
                </li>
                <li className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-3">
                  <span className="text-red-900 inline-flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    Bâtiments sans documents obligatoires
                  </span>
                  <span className="font-bold text-red-900 tabular-nums">
                    {formatNumber(data.conformite_erp.batiments_sans_documents_obligatoires)}
                  </span>
                </li>
                {data.conformite_erp.batiments_sans_documents_obligatoires === 0 &&
                  data.conformite_erp.documents_expirants_30j === 0 && (
                    <li className="flex items-center justify-center bg-green-50 border border-green-200 rounded-lg p-3 text-green-700">
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Aucune alerte documentaire.
                    </li>
                  )}
              </ul>
              <p className="text-[11px] text-gray-500 mt-3">
                Documents obligatoires : plan d'évacuation, rapport de contrôle.
              </p>
            </Panel>
          </div>

          <p className="text-[10px] text-gray-400 text-right pt-2">
            Généré le {new Date().toLocaleString('fr-FR')}
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Aucune donnée disponible.</p>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string;
  icon: typeof Building2;
  tone: 'primary' | 'blue' | 'purple' | 'orange';
}) {
  const TONE: Record<typeof tone, string> = {
    primary: 'text-primary-600',
    blue: 'text-blue-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${TONE[tone]}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${TONE[tone]}`}>{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${
        right ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
